import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { loadAllArticles } from '@/services/articleLoader';
import { MainNavigation } from '@/components/MainNavigation';
import { Footer } from '@/components/Footer';
import { Star, Award, ChevronRight } from 'lucide-react';
import { getArticleImages, getOptimizedSearchQuery } from '@/services/imageService';

export const FeaturedArticlesPage = () => {
    const [loadedArticles, setLoadedArticles] = useState([]);
    const [featuredImages, setFeaturedImages] = useState({});

    useEffect(() => {
        const loadArticles = async () => {
            try {
                const articles = await loadAllArticles();
                if (articles && articles.length > 0) {
                    // Filter for Pillar Articles or explicit 'Featured' category
                    const pillarArticles = articles.filter(a =>
                        a.layoutType === 'pillar' ||
                        a.category === 'IPTV Guides' ||
                        a.category === 'Featured'
                    );
                    setLoadedArticles(pillarArticles);
                }
            } catch (error) {
                console.error('Error loading articles', error);
            }
        };
        loadArticles();
    }, []);

    // Fetch images for these articles
    useEffect(() => {
        const fetchImages = async () => {
            if (loadedArticles.length === 0) return;
            const images = {};

            for (const article of loadedArticles) {
                try {
                    const searchQuery = getOptimizedSearchQuery(article.title);
                    const result = await getArticleImages(searchQuery, 1);
                    images[article.id] = result.length > 0 ? result[0] : { placeholder: true, color: 'from-green-600 to-emerald-900' };
                } catch (e) {
                    images[article.id] = { placeholder: true, color: 'from-green-600 to-emerald-900' };
                }
            }
            setFeaturedImages(images);
        };
        fetchImages();
    }, [loadedArticles]);

    const getFeaturedImage = (article) => featuredImages[article.id] || { placeholder: true, color: 'from-green-600 to-emerald-900' };

    return (
        <>
            <Helmet>
                <title>Featured IPTV Articles & Ultimate Guides | XtreamGate</title>
                <meta name="description" content="Explore our curated collection of comprehensive IPTV guides, pillar articles, and deep-dive comparisons." />
            </Helmet>

            <MainNavigation />

            <div className="min-h-screen bg-gradient-to-b from-background via-background to-green-950/20">
                <section className="pt-32 pb-16 px-4 text-center overflow-hidden relative">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl">
                        <div className="absolute top-20 left-1/4 w-[400px] h-[400px] bg-green-500/10 rounded-full blur-[100px]" />
                    </div>
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative z-10">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-full mb-6">
                            <Star className="w-4 h-4 text-green-400" />
                            <span className="text-sm font-bold text-green-400 uppercase tracking-wider">Premium Content</span>
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold mb-6">
                            Featured <span className="text-green-500">Collection</span>
                        </h1>
                        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                            Our most comprehensive guides, in-depth reviews, and essential pillar content designed to master your streaming experience.
                        </p>
                    </motion.div>
                </section>

                <div className="max-w-7xl mx-auto px-4 pb-24">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {loadedArticles.map((article, index) => {
                            const imageData = getFeaturedImage(article);
                            return (
                                <motion.div
                                    key={article.id}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    <Link to={article.canonicalPath || `/blog/${article.slug}`} className="group block h-full bg-card/40 border border-white/5 rounded-2xl overflow-hidden hover:border-green-500/50 hover:shadow-2xl hover:shadow-green-500/10 transition-all duration-300">
                                        <div className={`h-48 relative overflow-hidden ${imageData.placeholder ? `bg-gradient-to-br ${imageData.color}` : 'bg-gray-900'}`}>
                                            {imageData.url && <img src={imageData.url} alt={article.title} className="w-full h-full object-cover group-hover:scale-110 transition duration-700" />}
                                            <div className="absolute bottom-0 left-0 w-full p-4 bg-gradient-to-t from-black/80 to-transparent">
                                                <div className="flex items-center gap-2 text-white/90 font-medium">
                                                    <Award className="w-4 h-4 text-green-400" />
                                                    {article.category}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="p-6">
                                            <h3 className="text-xl font-bold mb-3 group-hover:text-green-400 transition">{article.title}</h3>
                                            <p className="text-sm text-muted-foreground line-clamp-3 mb-4">{article.excerpt}</p>
                                            <div className="flex items-center text-sm font-semibold text-green-500">
                                                View Solution <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                                            </div>
                                        </div>
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
                <Footer />
            </div>
        </>
    );
};
